import {
  AlertDialogOverlay,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  HStack,
  useDisclosure,
  useToast,
  Textarea,
  Skeleton,
} from '@chakra-ui/react';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useSubmitFundingRequestMutation } from 'types/dashboard-core-gqlTypes.generated.d';

import { CancelFundingRequestButton } from '../shared/CancelFundingRequestButton';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useGetCreditFacilityCapitalProviderRepresentativeQuery } from '~/packages/credit-facility/requests/requests.gql.generated';
import {
  FundingRequestContextValue,
  ContextValueMutable,
  useFundingRequestTemplateContextV1,
} from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

const SubmitRequestControlsInner = ({
  canCancelOnPending,
  creditFacility: {
    id: creditFacilityId,
    name: creditFacilityName,
    borrowerId,
  },
  context: {
    submission: { getPayload },
  },
  template,
}: {
  canCancelOnPending?: boolean;
  creditFacility: FundingRequestContextValue['creditFacility'];
  context: ContextValueMutable;
  template: FundingRequestContextValue['template'];
}) => {
  const { user } = useUserAuthOrThrow();

  // TODO @eldarshamukhamedov
  // FR attached comments are disabled for everyone except Prafin until we figure out how to
  // reliably look up the specific comment attached to an FR submission. The current approach is to
  // look up the latest comment, but that's not guaranteed to be the FR submission comment. Parafin
  // does not support EventLogs, so this still works for now.
  const isParafin = user.organization.slug === 'parafin';

  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cancelRef = useRef<HTMLButtonElement>(null);

  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);

  const [comment, setComment] = React.useState<string>('');

  const capitalProviderRepresentativeOrganizationQuery = useGetCreditFacilityCapitalProviderRepresentativeQuery(
    { variables: { creditFacilityId } },
  );
  if (capitalProviderRepresentativeOrganizationQuery.error) {
    throw capitalProviderRepresentativeOrganizationQuery.error;
  }

  const [
    submitFundingRequestMutate,
    { loading: isLoading },
  ] = useSubmitFundingRequestMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      // reverted to toast on all errors for this mutation because customer uses toast as CTA for errors
      errorToastConfig.title =
        err.message ||
        'An unexpected error occurred. Please contact Finley for support.';
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      const fundingRequestId = data.submitFundingRequest.id;
      navigate(`/funding-requests/${fundingRequestId}`);
    },
  });

  useEffect(() => {
    if (validationErrors.length > 0) {
      const message = validationErrors.join('\n');
      toast({
        ...errorToastConfig,
        title: message,
      });
    }
  }, [validationErrors, toast]);

  useEffect(() => {
    const submissionPayload = getPayload();
    if (submissionPayload.isValid) {
      setValidationErrors([]);
    }
  }, [getPayload]);

  const handleClose = () => {
    setComment('');
    onClose();
  };

  const handleConfirmSubmit = () => {
    const submissionPayload = getPayload();

    if (!submissionPayload.isValid) {
      setValidationErrors(submissionPayload.errors);
      handleClose();
      return;
    }

    if (!submissionPayload.isValid) {
      return;
    }

    const commentPermissionedOrgs = capitalProviderRepresentativeOrganizationQuery
      .data?.creditFacility?.capitalProviderRepresentativeOrganization?.id
      ? [
          borrowerId,
          capitalProviderRepresentativeOrganizationQuery.data.creditFacility
            .capitalProviderRepresentativeOrganization.id,
        ]
      : [borrowerId];

    submitFundingRequestMutate({
      variables: {
        fundingRequestChangeset: {
          ...submissionPayload.payload,
          commentProps:
            comment && isParafin
              ? {
                  comment,
                  organizationIds: commentPermissionedOrgs,
                  isPinned: true,
                }
              : null,
        },
      },
    });
    handleClose();
  };

  if (
    capitalProviderRepresentativeOrganizationQuery.loading ||
    !capitalProviderRepresentativeOrganizationQuery.data
  ) {
    return <Skeleton height="2rem" />;
  }

  return (
    <HStack
      sx={{
        w: '100%',
        justifyContent: 'flex-end',
        gap: '1em',
      }}
    >
      {template?.currentFundingRequestComponents[0].fundingRequestId ? (
        <CancelFundingRequestButton
          canCancelOnPending={canCancelOnPending}
          fundingRequestStatus="pending"
          fundingRequestId={
            template.currentFundingRequestComponents[0].fundingRequestId
          }
          creditFacilityId={creditFacilityId}
          isDisabled={isLoading}
        />
      ) : (
        <Button
          variant="secondary-muted"
          colorScheme="v2-aurora"
          onClick={() => navigate('/funding-requests')}
        >
          Cancel
        </Button>
      )}
      <Button
        variant="primary"
        colorScheme="v2-aurora"
        isLoading={isLoading}
        onClick={onOpen}
      >
        Submit
      </Button>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Submit Funding Request
            </AlertDialogHeader>

            <AlertDialogBody>
              {commonUtils.EnvUtils.isCustomerFacing() ? (
                <Typography variant="base">{`Are you sure you want to submit the Funding Request for ${creditFacilityName}?`}</Typography>
              ) : (
                <>
                  <Typography variant="base">{`Are you sure you want to submit the Funding Request for ${creditFacilityName}?`}</Typography>
                  {isParafin ? (
                    <>
                      <Typography variant="base">
                        If so, you can include a message that will be attached
                        in the email.
                      </Typography>
                      <Textarea
                        mt="1rem"
                        value={comment}
                        placeholder="Write a message"
                        onChange={(e) => setComment(e.target.value)}
                      />
                    </>
                  ) : null}
                </>
              )}
            </AlertDialogBody>

            <AlertDialogFooter sx={{ gap: '1em' }}>
              <Button
                variant="secondary-muted"
                colorScheme="v2-aurora"
                ref={cancelRef}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                colorScheme="v2-aurora"
                onClick={handleConfirmSubmit}
                isLoading={isLoading}
              >
                Submit
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </HStack>
  );
};

export const SubmitRequestControls = ({
  canCancelOnPending,
}: {
  canCancelOnPending?: boolean;
}) => {
  const {
    context,
    creditFacility,
    template,
  } = useFundingRequestTemplateContextV1();

  return context.allowedActions === 'mutable' ? (
    <SubmitRequestControlsInner
      context={context}
      creditFacility={creditFacility}
      template={template}
      canCancelOnPending={canCancelOnPending}
    />
  ) : null;
};
